import type { ReactNode } from 'react'

export interface BadgeProps {
  children: ReactNode
  variant?: 'default' | 'primary' | 'secondary' | 'accent' | 'ghost' | 'success' | 'warning' | 'error' | 'info' | 'count'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  outline?: boolean
  className?: string
  title?: string
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  outline = false,
  className = '',
  title,
}: BadgeProps) {
  const variantClasses = {
    default: '',
    primary: 'badge-primary',
    secondary: 'badge-secondary',
    accent: 'badge-accent',
    ghost: 'badge-ghost',
    success: 'badge-success',
    warning: 'badge-warning',
    error: 'badge-error',
    info: 'badge-info',
    // Color fixed to primary deliberately: all 9 current count-pill call sites (reports
    // filter-counts, sidebar, bank-recon tab) use primary — revisit only if a non-primary
    // count appears. rounded-full (vs the --radius-selector 0.375rem default) is what
    // visually distinguishes a count pill from a status chip; min-w-5 pairs with the
    // existing min-h-5 floor so a single digit renders as a true circle.
    count: 'badge-primary rounded-full tabular-nums justify-center min-w-5 px-1',
  }

  const sizeClasses = {
    xs: 'badge-xs',
    sm: 'badge-sm',
    md: '',
    lg: 'badge-lg',
  }

  return (
    <span
      className={`badge h-auto min-h-5 py-0.5 leading-tight whitespace-nowrap ${variantClasses[variant]} ${sizeClasses[size]} ${outline ? 'badge-outline' : ''} ${className}`}
      title={title}
    >
      {/* 99+ capping (or any other display-count formatting) is the caller's job — Badge
          renders children as-is and does not clamp or reformat numeric content. */}
      {children}
    </span>
  )
}
